"use client";

import { Button } from "@/components/rac/button";
import { Carousel, CarouselApi, CarouselContent, CarouselItem } from "@/components/rac/carousel";
import { useScrollAnimation } from "@/hooks/use-scroll-animation";
import { useTestimonialsRefresh } from "@/hooks/use-testimonials-refresh";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { m } from "framer-motion";
import { RefreshCw } from "lucide-react";
import React, { useEffect, useMemo, useState } from "react";
import { TestimonialCard } from "./card";
import { Testimonial, transformReviewToTestimonial } from "./index";

// Extend dayjs with relativeTime plugin
dayjs.extend(relativeTime);

interface TestimonialsClientProps {
	testimonials: Testimonial[];
}

export const TestimonialsClient: React.FC<TestimonialsClientProps> = ({ testimonials }) => {
	const { ref: titleRef } = useScrollAnimation<HTMLHeadingElement>({ triggerOnce: true });
	const [api, setApi] = React.useState<CarouselApi>();
	const [currentTestimonials, setCurrentTestimonials] = useState<Testimonial[]>(testimonials);

	const {
		testimonials: refreshedReviews,
		isLoading,
		refreshTestimonials,
	} = useTestimonialsRefresh({
		limit: 10,
	});

	// Transform refreshed reviews data to testimonials format
	const refreshedTestimonials = useMemo(() => {
		return refreshedReviews.map(transformReviewToTestimonial);
	}, [refreshedReviews]);

	// Update current testimonials when new ones are fetched
	useEffect(() => {
		if (refreshedTestimonials.length > 0) {
			setCurrentTestimonials(refreshedTestimonials);
			// // Reset carousel to first slide after refresh
			// if (api) {
			// 	api.scrollTo(0);
			// }
		}
	}, [refreshedTestimonials, api]);

	const handleRefresh = async () => {
		await refreshTestimonials();
	};

	return (
		<>
			<div ref={titleRef} className="max-w-4xl mx-auto text-center mb-12">
				<m.h2
					id="testimonials-heading"
					className="text-3xl md:text-4xl font-bold mb-4"
					initial={{ opacity: 0, y: 20 }}
					animate={{ opacity: 1, y: 0 }}
					transition={{ duration: 0.4, ease: "easeOut" }}
				>
					What Our Customers Say
				</m.h2>
				<p className="text-muted-foreground max-w-2xl mx-auto">
					Read from our satisfied customers about their experience with our services.
				</p>
			</div>

			<a
				href="https://share.google/TypTi4R5U0dhoCRBC"
				target="_blank"
				rel="noopener noreferrer"
				className="relative max-w-6xl mx-auto px-4"
			>
				<div className="relative">
					{/* Refresh button */}
					<div className="flex justify-end mb-1">
						<Button
							onClick={handleRefresh}
							isDisabled={isLoading}
							className="flex items-center gap-2"
							aria-label="Load different reviews"
							variant="outline"
						>
							<RefreshCw className={`w-4 h-4 ${isLoading ? "animate-spin" : ""}`} />
							<span className="text-sm font-medium">More Reviews</span>
						</Button>
					</div>

					<Carousel opts={{ align: "center", loop: true, slidesToScroll: 1 }} className="w-full" setApi={setApi}>
						<CarouselContent className="-ml-2 md:-ml-4">
							{currentTestimonials.map((testimonial, index) => {
								// Show partial cards on both sides, with basis sized to allow partial visibility
								const basisClass = "basis-[80%] sm:basis-[70%] md:basis-[60%]";

								return (
									<CarouselItem key={testimonial.id} className={`pl-2 md:pl-4 ${basisClass}`}>
										<div className="h-full px-1" onClick={() => api?.scrollTo(index)}>
											<TestimonialCard
												testimonial={testimonial}
												className="transition-all duration-300 hover:shadow-md cursor-pointer"
											/>
										</div>
									</CarouselItem>
								);
							})}
						</CarouselContent>
					</Carousel>
				</div>
			</a>
		</>
	);
};
